import React, { useEffect, useRef, useState } from 'react';
import { Party, PartyMessage, sendPartyMessage } from '../services/watchParty';
import { IconParty } from './icons';
import './WatchParty.css';

interface WatchPartyProps {
  party: Party;
  participantId: string;
  isHost: boolean;
  onLeave: () => void;
  onClose: () => void;
}

export const WatchParty: React.FC<WatchPartyProps> = ({
  party,
  participantId,
  isHost,
  onLeave,
  onClose,
}) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [party.messages.length]);

  const handleSend = async () => {
    const value = text.trim();
    if (!value || sending) return;
    setSending(true);
    try {
      await sendPartyMessage(party.code, participantId, value);
      setText('');
    } catch (err) {
      console.error('Failed to send party message', err);
    } finally {
      setSending(false);
    }
  };

  const copyInvite = () => {
    const url = `${window.location.origin}/party/${party.code}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  return (
    <div className="watch-party">
      <div className="watch-party-header">
        <div className="watch-party-title">
          <IconParty size={14} className="watch-party-icon" />
          <span>Watch Party</span>
          <span className="watch-party-code">{party.code}</span>
        </div>
        <div className="watch-party-header-actions">
          <button className="watch-party-btn" onClick={copyInvite} title="Copy invite link">
            {copied ? 'Copied!' : 'Invite'}
          </button>
          <button className="watch-party-btn danger" onClick={onLeave} title="Leave party">
            Leave
          </button>
          <button className="watch-party-close" onClick={onClose} title="Hide party panel">×</button>
        </div>
      </div>

      <div className="watch-party-participants">
        {party.participants.map(p => (
          <span key={p.id} className={`party-chip ${p.isHost ? 'host' : ''} ${p.id === participantId ? 'me' : ''}`} title={p.isHost ? 'Host' : 'Guest'}>
            {p.isHost && '★ '}
            {p.nickname}
            {p.id === participantId && ' (you)'}
          </span>
        ))}
      </div>

      <div className="watch-party-status">
        {isHost ? (
          <>You're the <strong>host</strong>. Manual picks and auto-switch changes both sync everyone to the same stream.</>
        ) : (
          <>Following <strong>{party.hostNickname}</strong>. Only the host can change streams.</>
        )}
      </div>

      <div className="watch-party-messages" ref={listRef}>
        {party.messages.length === 0 ? (
          <div className="watch-party-empty">No messages yet — say hi!</div>
        ) : (
          party.messages.map((m: PartyMessage) => (
            <div key={m.id} className={`party-msg ${m.participantId === participantId ? 'own' : ''}`}>
              <span className="party-msg-nick">{m.nickname}:</span>
              <span className="party-msg-text">{m.text}</span>
            </div>
          ))
        )}
      </div>

      <form
        className="watch-party-input"
        onSubmit={e => { e.preventDefault(); handleSend(); }}
      >
        <input
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Message the party…"
          maxLength={400}
          disabled={sending}
        />
        <button type="submit" disabled={!text.trim() || sending}>Send</button>
      </form>
    </div>
  );
};

interface WatchPartyLauncherProps {
  onCreate: (nickname: string) => Promise<void> | void;
  onJoin: (code: string, nickname: string) => Promise<void> | void;
  onClose: () => void;
  defaultNickname?: string;
  defaultCode?: string;
  busy?: boolean;
  error?: string | null;
}

export const WatchPartyLauncher: React.FC<WatchPartyLauncherProps> = ({
  onCreate,
  onJoin,
  onClose,
  defaultNickname,
  defaultCode,
  busy,
  error,
}) => {
  const [nickname, setNickname] = useState(defaultNickname || '');
  const [code, setCode] = useState(defaultCode || '');
  const [mode, setMode] = useState<'create' | 'join'>(defaultCode ? 'join' : 'create');

  const submit = () => {
    if (busy) return;
    if (mode === 'create') {
      onCreate(nickname.trim() || 'Host');
    } else {
      const clean = code.trim().toUpperCase();
      if (clean.length < 4) return;
      onJoin(clean, nickname.trim() || 'Guest');
    }
  };

  return (
    <div className="watch-party-modal" onClick={onClose}>
      <div className="watch-party-modal-content" onClick={e => e.stopPropagation()}>
        <div className="watch-party-modal-header">
          <h2><IconParty size={18} /> Watch Party</h2>
          <button className="watch-party-close" onClick={onClose}>×</button>
        </div>

        <p className="watch-party-sub">
          Watch the hottest streams together. The host controls the stream, everyone follows live, and host auto-switches stay in sync.
        </p>

        <div className="watch-party-tabs">
          <button className={mode === 'create' ? 'active' : ''} onClick={() => setMode('create')}>Create</button>
          <button className={mode === 'join' ? 'active' : ''} onClick={() => setMode('join')}>Join</button>
        </div>

        <div className="watch-party-form">
          <label>
            Nickname
            <input
              type="text"
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              placeholder="Your display name"
              maxLength={20}
            />
          </label>

          {mode === 'join' && (
            <label>
              Party code
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={8}
              />
            </label>
          )}

          {error && <div className="watch-party-error">{error}</div>}

          <button
            className="watch-party-submit"
            onClick={submit}
            disabled={busy || (mode === 'join' && code.trim().length < 4)}
          >
            {busy ? 'Working…' : mode === 'create' ? 'Start Watch Party' : 'Join Watch Party'}
          </button>
        </div>
      </div>
    </div>
  );
};
